import { Box } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useRef } from 'react';

interface BaseItem {
  name: string;
  id: string;
}

export const VirtualizedList = <T extends BaseItem>({
  list,
  isLoading,
  renderListItem,
}: {
  list: T[];
  isLoading?: boolean;
  renderListItem: (item: T, index: number) => React.ReactNode;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Here we must use tanstack-virtual because the list of organizations could have
  // 500 to 1000 items. I tried rendering a list of this size without the virtualizer
  // and the performance was unacceptable.
  const virtualizer = useVirtualizer({
    count: list.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 45,
    getItemKey: (index) => list[index].id,
  });

  if (isLoading) {
    return <Skeleton h="400px" mt="0.4em" data-testid="skeleton" />;
  }

  return (
    <Box
      ref={scrollRef}
      height="25rem"
      overflowY="auto"
      mt="0.5rem"
      data-testid="virtualized-list"
    >
      <Box height={`${virtualizer.getTotalSize()}px`} position="relative">
        {virtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItem.start}px)`,
            }}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
          >
            {renderListItem(list[virtualItem.index], virtualItem.index)}
          </div>
        ))}
      </Box>
    </Box>
  );
};
